import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API, resolveImageUrl } from "../App";
import { useAuth } from "../App";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Package, ShoppingCart, Users, DollarSign, Plus, Pencil, Trash2, Upload, Tag, Settings, ArrowLeft, Star } from "lucide-react";

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAdminData = useCallback(async () => {
    try {
      const [statsRes, productsRes, ordersRes, customersRes, couponsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/products`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true }),
        axios.get(`${API}/admin/customers`, { withCredentials: true }),
        axios.get(`${API}/coupons`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setCustomers(customersRes.data);
      setCoupons(couponsRes.data);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdminData(); }, [fetchAdminData]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent"></div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-zinc-900 text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-zinc-400 hover:text-white"><ArrowLeft size={20} /></Link>
          <h1 className="text-lg font-semibold" data-testid="admin-title">Rivora Admin</h1>
        </div>
        <p className="text-sm text-zinc-400">{user?.email}</p>
      </div>

      <div className="px-6 md:px-8 max-w-7xl mx-auto py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Package className="text-zinc-500" size={20} /><div><p className="text-2xl font-bold">{stats?.total_products || 0}</p><p className="text-xs text-zinc-500">Products</p></div></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><ShoppingCart className="text-zinc-500" size={20} /><div><p className="text-2xl font-bold">{stats?.total_orders || 0}</p><p className="text-xs text-zinc-500">Orders</p></div></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><Users className="text-zinc-500" size={20} /><div><p className="text-2xl font-bold">{stats?.total_customers || 0}</p><p className="text-xs text-zinc-500">Customers</p></div></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><DollarSign className="text-zinc-500" size={20} /><div><p className="text-2xl font-bold">₹{(stats?.total_revenue || 0).toLocaleString()}</p><p className="text-xs text-zinc-500">Revenue</p></div></div></CardContent></Card>
        </div>

        <Tabs defaultValue="products" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="products" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="tab-coupons">Coupons</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            <ProductsTab products={products} onRefresh={fetchAdminData} />
          </TabsContent>
          <TabsContent value="orders">
            <OrdersTab orders={orders} onRefresh={fetchAdminData} />
          </TabsContent>
          <TabsContent value="customers">
            <CustomersTab customers={customers} />
          </TabsContent>
          <TabsContent value="coupons">
            <CouponsTab coupons={coupons} onRefresh={fetchAdminData} />
          </TabsContent>
          <TabsContent value="reviews">
            <ReviewsTab />
          </TabsContent>
          <TabsContent value="content">
            <ContentTab />
          </TabsContent>
          <TabsContent value="messages">
            <MessagesTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ─── Reviews Tab ────────────────────────────────────────────────────────────

function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRating, setFilterRating] = useState("all");

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, { withCredentials: true });
      setReviews(data);
    } catch (error) {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const handleDelete = async (reviewId) => {
    if (!window.confirm("Delete this review? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, { withCredentials: true });
      toast.success("Review deleted");
      setReviews(prev => prev.filter(r => r.review_id !== reviewId));
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const filtered = filterRating === "all"
    ? reviews
    : reviews.filter(r => r.rating === parseInt(filterRating));

  const StarDisplay = ({ rating }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={14}
          className={i <= rating ? "text-yellow-400 fill-yellow-400" : "text-zinc-300 fill-zinc-300"}
        />
      ))}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <CardTitle>Reviews ({reviews.length})</CardTitle>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-zinc-500 whitespace-nowrap">Filter by rating:</Label>
            <Select value={filterRating} onValueChange={setFilterRating}>
              <SelectTrigger className="w-36" data-testid="reviews-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ratings</SelectItem>
                <SelectItem value="5">★★★★★ (5)</SelectItem>
                <SelectItem value="4">★★★★☆ (4)</SelectItem>
                <SelectItem value="3">★★★☆☆ (3)</SelectItem>
                <SelectItem value="2">★★☆☆☆ (2)</SelectItem>
                <SelectItem value="1">★☆☆☆☆ (1)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent"></div>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-zinc-500 text-center py-10">No reviews found</p>
        ) : (
          <div className="space-y-3">
            {filtered.map((review) => (
              <div
                key={review.review_id}
                className="flex gap-4 items-start p-4 border rounded-lg hover:bg-zinc-50 transition-colors"
                data-testid={`admin-review-${review.review_id}`}
              >
                {/* Product image */}
                {review.product?.image_url && (
                  <img
                    src={resolveImageUrl(review.product.image_url)}
                    alt={review.product?.name}
                    className="w-14 h-14 object-cover rounded shrink-0"
                  />
                )}

                {/* Review details */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <StarDisplay rating={review.rating} />
                    <span className="text-xs text-zinc-400">•</span>
                    <span className="text-sm font-medium text-zinc-700 truncate">
                      {review.product?.name || "Unknown Product"}
                    </span>
                  </div>
                  {review.comment && (
                    <p className="text-sm text-zinc-600 mb-1 line-clamp-2">{review.comment}</p>
                  )}
                  <p className="text-xs text-zinc-400">
                    By <span className="font-medium text-zinc-500">{review.user?.name || review.user?.email || "Anonymous"}</span>
                    {review.created_at && (
                      <> &bull; {new Date(review.created_at).toLocaleDateString()}</>
                    )}
                  </p>
                </div>

                {/* Delete button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(review.review_id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                  data-testid={`delete-review-${review.review_id}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Existing Tabs (unchanged) ───────────────────────────────────────────────

function ProductsTab({ products, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", category: "Earrings", price: "", compare_at_price: "", image_url: "", additional_images: [], stock: 100, is_best_seller: false });
  const formRef = useRef(null);

  const resetForm = () => {
    setForm({ name: "", description: "", category: "Earrings", price: "", compare_at_price: "", image_url: "", additional_images: [], stock: 100, is_best_seller: false });
    setEditProduct(null);
    setShowForm(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/upload/image`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      const imageUrl = data.url; // Cloudinary direct CDN URL
      setForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success("Image uploaded!");
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleAdditionalImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAdditional(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/upload/image`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      const imageUrl = data.url; // Cloudinary direct CDN URL
      setForm(prev => ({ ...prev, additional_images: [...prev.additional_images, imageUrl] }));
      toast.success("Image added!");
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploadingAdditional(false);
      e.target.value = "";
    }
  };

  const removeAdditionalImage = (index) => {
    setForm(prev => ({ ...prev, additional_images: prev.additional_images.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price), compare_at_price: form.compare_at_price ? parseFloat(form.compare_at_price) : null, stock: parseInt(form.stock) };
      if (editProduct) {
        await axios.put(`${API}/products/${editProduct.product_id}`, payload, { withCredentials: true });
        toast.success("Product updated!");
      } else {
        await axios.post(`${API}/products`, payload, { withCredentials: true });
        toast.success("Product added!");
      }
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error("Failed to save product");
    }
  };

  const handleEdit = (product) => {
    setForm({ name: product.name, description: product.description, category: product.category, price: String(product.price), compare_at_price: product.compare_at_price ? String(product.compare_at_price) : "", image_url: product.image_url, additional_images: product.additional_images || [], stock: product.stock, is_best_seller: product.is_best_seller });
    setEditProduct(product);
    setShowForm(true);
    // Scroll to form after React renders it
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      toast.success("Product deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Products ({products.length})</CardTitle>
          <Button onClick={() => { resetForm(); setShowForm(!showForm); }} data-testid="add-product-button">
            <Plus size={16} className="mr-2" />{showForm ? "Cancel" : "Add Product"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showForm && (
          <div ref={formRef} className="mb-6 p-5 border rounded-lg bg-zinc-50 space-y-4" data-testid="product-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Product Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="product-name-input" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(val) => setForm({...form, category: val})}>
                  <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Earrings">Earrings</SelectItem>
                    <SelectItem value="Rings">Rings</SelectItem>
                    <SelectItem value="Bracelets">Bracelets</SelectItem>
                    <SelectItem value="Pendant Chains">Pendant Chains</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="product-desc-input" /></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label>Price (₹)</Label><Input type="number" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} data-testid="product-price-input" /></div>
              <div><Label>Compare At Price (₹)</Label><Input type="number" value={form.compare_at_price} onChange={(e) => setForm({...form, compare_at_price: e.target.value})} placeholder="Optional" data-testid="product-compare-price" /></div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})} data-testid="product-stock-input" /></div>
            </div>
            <div>
              <Label>Product Images</Label>
              <div className="flex items-center gap-4 mt-1">
                <Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="Primary image URL or upload" className="flex-1" data-testid="product-image-url" />
                <label className="cursor-pointer inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 rounded text-sm hover:bg-zinc-800">
                  <Upload size={16} />{uploading ? "Uploading..." : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={uploading} data-testid="product-image-upload" />
                </label>
              </div>
              {(form.image_url || form.additional_images.length > 0) && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.image_url && (
                    <div className="relative">
                      <img src={resolveImageUrl(form.image_url)} alt="Primary" className="w-20 h-20 object-cover rounded border-2 border-zinc-900" />
                      <span className="absolute bottom-0 left-0 right-0 text-center text-[10px] bg-zinc-900 text-white rounded-b">Main</span>
                    </div>
                  )}
                  {form.additional_images.map((url, index) => (
                    <div key={index} className="relative group">
                      <img src={resolveImageUrl(url)} alt={`Extra ${index + 1}`} className="w-20 h-20 object-cover rounded border" />
                      <button
                        type="button"
                        onClick={() => removeAdditionalImage(index)}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-additional-image-${index}`}
                      >×</button>
                    </div>
                  ))}
                  <label className="cursor-pointer w-20 h-20 border-2 border-dashed border-zinc-300 rounded flex flex-col items-center justify-center text-zinc-400 hover:border-zinc-500 hover:text-zinc-600 transition-colors">
                    <Upload size={16} />
                    <span className="text-[10px] mt-1">{uploadingAdditional ? "..." : "Add more"}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={handleAdditionalImageUpload} disabled={uploadingAdditional} data-testid="product-additional-image-upload" />
                  </label>
                </div>
              )}
              {!form.image_url && form.additional_images.length === 0 && (
                <p className="text-xs text-zinc-400 mt-1">Upload a primary image above, then add more by clicking "Add more"</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_best_seller} onCheckedChange={(val) => setForm({...form, is_best_seller: val})} data-testid="product-bestseller-switch" />
              <Label>Best Seller</Label>
            </div>
            <Button onClick={handleSave} data-testid="save-product-button">{editProduct ? "Update Product" : "Save Product"}</Button>
          </div>
        )}
        <div className="space-y-2">
          {products.map((product) => (
            <div key={product.product_id} className="flex gap-4 p-3 border rounded-lg hover:bg-zinc-50 items-center" data-testid={`admin-product-${product.product_id}`}>
              <img src={resolveImageUrl(product.image_url)} alt="" className="w-14 h-14 object-cover rounded" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium truncate">{product.name}</p>
                  {product.is_best_seller && <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Best Seller</span>}
                  {!product.is_active && <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">Inactive</span>}
                </div>
                <p className="text-sm text-zinc-500">{product.category} &bull; ₹{product.price} &bull; Stock: {product.stock}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(product)} data-testid={`edit-product-${product.product_id}`}><Pencil size={14} /></Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(product.product_id)} className="text-red-600 hover:text-red-700" data-testid={`delete-product-${product.product_id}`}><Trash2 size={14} /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function OrdersTab({ orders, onRefresh }) {
  const handleStatusUpdate = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status }, { withCredentials: true });
      toast.success("Order status updated");
      onRefresh();
    } catch (error) {
      toast.error("Failed to update order");
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle>Orders ({orders.length})</CardTitle></CardHeader>
      <CardContent>
        {orders.length === 0 ? <p className="text-zinc-500 text-center py-8">No orders yet</p> : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.order_id} className="border rounded-lg p-4" data-testid={`admin-order-${order.order_id}`}>
                <div className="flex flex-col md:flex-row justify-between mb-3">
                  <div>
                    <p className="font-medium">#{order.order_id}</p>
                    <p className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleString()}</p>
                    {order.address && (
                      <p className="text-xs text-zinc-600 mt-0.5 font-medium">
                        👤 {order.address.full_name} &bull; 📞 {order.address.phone}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold">₹{order.total?.toLocaleString()}</p>
                    <div className="flex gap-2 mt-1 justify-end">
                      <span className={`text-xs px-2 py-0.5 rounded ${order.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{order.payment_status}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-zinc-100">{order.payment_method}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-zinc-50 rounded px-2 py-1">
                      <img src={resolveImageUrl(item.product?.image_url)} alt="" className="w-8 h-8 object-cover rounded" />
                      <span className="text-xs">{item.product?.name || item.name} ×{item.quantity}</span>
                    </div>
                  ))}
                </div>
                {order.address && (
                  <p className="text-xs text-zinc-500 mb-3">
                    📍 {order.address.address_line1}, {order.address.city}, {order.address.state} – {order.address.pincode}
                  </p>
                )}
                {order.coupon_code && <p className="text-xs text-zinc-500 mb-2">Coupon: {order.coupon_code} (Discount: ₹{order.discount})</p>}
                <Select value={order.order_status} onValueChange={(val) => handleStatusUpdate(order.order_id, val)}>
                  <SelectTrigger className="w-48" data-testid={`order-status-${order.order_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CustomersTab({ customers }) {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchCustomerDetail = async (userId) => {
    setLoadingDetail(true);
    try {
      const { data } = await axios.get(`${API}/admin/customers/${userId}`, { withCredentials: true });
      setCustomerDetail(data);
      setSelectedCustomer(userId);
    } catch {
      toast.error("Failed to load customer details");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleBack = () => {
    setSelectedCustomer(null);
    setCustomerDetail(null);
  };

  // ── Customer detail view ───────────────────────────────────────────────────
  if (selectedCustomer && customerDetail) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={handleBack}>← Back</Button>
            <CardTitle>{customerDetail.name || customerDetail.email}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-zinc-50 rounded-lg p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Email</p>
              <p className="text-sm font-medium break-all">{customerDetail.email}</p>
            </div>
            <div className="bg-zinc-50 rounded-lg p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Phone</p>
              <p className="text-sm font-medium">{customerDetail.phone || '—'}</p>
            </div>
            <div className="bg-zinc-50 rounded-lg p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Joined</p>
              <p className="text-sm font-medium">{new Date(customerDetail.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="border rounded-lg p-4 text-center">
              <p className="text-2xl font-bold">{customerDetail.orders?.length || 0}</p>
              <p className="text-xs text-zinc-500 mt-1">Total Orders</p>
            </div>
            <div className="border rounded-lg p-4 text-center">
              <p className="text-2xl font-bold">₹{(customerDetail.total_spent || 0).toLocaleString()}</p>
              <p className="text-xs text-zinc-500 mt-1">Total Spent</p>
            </div>
            <div className="border rounded-lg p-4 text-center">
              <p className="text-2xl font-bold">{customerDetail.addresses?.length || 0}</p>
              <p className="text-xs text-zinc-500 mt-1">Saved Addresses</p>
            </div>
          </div>

          {/* Orders */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-600 mb-3">Order History</h3>
            {customerDetail.orders?.length === 0 ? (
              <p className="text-zinc-400 text-sm">No orders yet</p>
            ) : (
              <div className="space-y-2">
                {customerDetail.orders.map((order) => (
                  <div key={order.order_id} className="flex items-center justify-between border rounded-lg p-3">
                    <div>
                      <p className="text-sm font-medium">#{order.order_id}</p>
                      <p className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleDateString('en-IN')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">₹{order.total?.toLocaleString()}</p>
                      <div className="flex gap-1 justify-end mt-0.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${order.order_status === 'delivered' ? 'bg-green-100 text-green-700' : order.order_status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {order.order_status}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">{order.payment_method}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Addresses */}
          {customerDetail.addresses?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-zinc-600 mb-3">Saved Addresses</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {customerDetail.addresses.map((addr) => (
                  <div key={addr.address_id} className="border rounded-lg p-3 text-sm text-zinc-600">
                    <p className="font-medium text-zinc-900">{addr.full_name}</p>
                    <p>{addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}</p>
                    <p>{addr.city}, {addr.state} – {addr.pincode}</p>
                    <p>📞 {addr.phone}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // ── Customer list view ─────────────────────────────────────────────────────
  return (
    <Card>
      <CardHeader><CardTitle>Customers ({customers.length})</CardTitle></CardHeader>
      <CardContent>
        {loadingDetail && (
          <div className="flex justify-center py-8">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent"></div>
          </div>
        )}
        {customers.length === 0 ? (
          <p className="text-zinc-500 text-center py-8">No customers yet</p>
        ) : (
          <div className="space-y-2">
            {customers.map((customer) => (
              <button
                key={customer.user_id}
                onClick={() => fetchCustomerDetail(customer.user_id)}
                className="w-full text-left border rounded-lg p-4 hover:bg-zinc-50 hover:border-zinc-400 transition-all"
                data-testid={`admin-customer-${customer.user_id}`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{customer.name || "No name"}</p>
                    <p className="text-sm text-zinc-500">{customer.email}</p>
                    {customer.phone && <p className="text-sm text-zinc-400">{customer.phone}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-400">Joined {new Date(customer.created_at).toLocaleDateString('en-IN')}</p>
                    <p className="text-xs text-zinc-400 mt-1">Click to view details →</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CouponsTab({ coupons, onRefresh }) {
  const emptyForm = { code: "", discount_type: "percentage", discount_value: "", min_order_value: "", expiry_date: "", show_on_checkout: false };

  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState(emptyForm);

  // Edit state — holds the coupon_id being edited, or null
  const [editingId, setEditingId]   = useState(null);
  const [editForm, setEditForm]     = useState(emptyForm);
  const [editSaving, setEditSaving] = useState(false);

  // ── Create ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      const payload = { ...form, discount_value: parseFloat(form.discount_value), min_order_value: form.min_order_value ? parseFloat(form.min_order_value) : 0, expiry_date: form.expiry_date || null };
      await axios.post(`${API}/coupons`, payload, { withCredentials: true });
      toast.success("Coupon created!");
      setShowForm(false);
      setForm(emptyForm);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create coupon");
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (couponId) => {
    if (!window.confirm("Delete this coupon?")) return;
    try {
      await axios.delete(`${API}/coupons/${couponId}`, { withCredentials: true });
      toast.success("Coupon deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete coupon");
    }
  };

  // ── Open edit form ────────────────────────────────────────────────────────
  const startEdit = (coupon) => {
    setEditingId(coupon.coupon_id);
    setEditForm({
      code:             coupon.code,
      discount_type:    coupon.discount_type,
      discount_value:   String(coupon.discount_value),
      min_order_value:  coupon.min_order_value > 0 ? String(coupon.min_order_value) : "",
      // Convert ISO date to datetime-local format (YYYY-MM-DDTHH:mm)
      expiry_date:      coupon.expiry_date
        ? coupon.expiry_date.substring(0, 16)
        : "",
      show_on_checkout: coupon.show_on_checkout || false,
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm(emptyForm); };

  // ── Save edit ─────────────────────────────────────────────────────────────
  const handleEditSave = async (couponId) => {
    if (!editForm.code || !editForm.discount_value) {
      toast.error("Code and discount value are required");
      return;
    }
    setEditSaving(true);
    try {
      const payload = {
        code:             editForm.code.toUpperCase(),
        discount_type:    editForm.discount_type,
        discount_value:   parseFloat(editForm.discount_value),
        min_order_value:  editForm.min_order_value ? parseFloat(editForm.min_order_value) : 0,
        expiry_date:      editForm.expiry_date || null,
        show_on_checkout: editForm.show_on_checkout,
      };
      await axios.put(`${API}/coupons/${couponId}`, payload, { withCredentials: true });
      toast.success("Coupon updated!");
      cancelEdit();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update coupon");
    } finally {
      setEditSaving(false);
    }
  };

  // ── Toggle helpers ────────────────────────────────────────────────────────
  const toggleActive = async (coupon) => {
    try {
      await axios.put(`${API}/coupons/${coupon.coupon_id}`, { is_active: !coupon.is_active }, { withCredentials: true });
      toast.success("Coupon updated");
      onRefresh();
    } catch (error) {
      toast.error("Failed to update coupon");
    }
  };

  const toggleShowOnCheckout = async (coupon) => {
    try {
      await axios.put(`${API}/coupons/${coupon.coupon_id}`, { show_on_checkout: !coupon.show_on_checkout }, { withCredentials: true });
      toast.success(coupon.show_on_checkout ? "Hidden from checkout" : "Now visible on checkout");
      onRefresh();
    } catch (error) {
      toast.error("Failed to update coupon");
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Coupons ({coupons.length})</CardTitle>
          <Button onClick={() => { setShowForm(!showForm); cancelEdit(); }} data-testid="add-coupon-button">
            <Tag size={16} className="mr-2" />{showForm ? "Cancel" : "Add Coupon"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* ── Create form ── */}
        {showForm && (
          <div className="mb-6 p-5 border rounded-lg bg-zinc-50 space-y-4" data-testid="coupon-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Coupon Code</Label><Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="e.g. RIVORA20" data-testid="coupon-code-input" /></div>
              <div><Label>Discount Type</Label>
                <Select value={form.discount_type} onValueChange={(val) => setForm({...form, discount_type: val})}>
                  <SelectTrigger data-testid="coupon-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label>Discount Value</Label><Input type="number" value={form.discount_value} onChange={(e) => setForm({...form, discount_value: e.target.value})} data-testid="coupon-value-input" /></div>
              <div><Label>Min Order Value (₹)</Label><Input type="number" value={form.min_order_value} onChange={(e) => setForm({...form, min_order_value: e.target.value})} placeholder="0" data-testid="coupon-min-order" /></div>
              <div><Label>Expiry Date</Label><Input type="datetime-local" value={form.expiry_date} onChange={(e) => setForm({...form, expiry_date: e.target.value})} data-testid="coupon-expiry-input" /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.show_on_checkout} onCheckedChange={(val) => setForm({...form, show_on_checkout: val})} id="show-on-checkout-new" />
              <Label htmlFor="show-on-checkout-new" className="cursor-pointer">Show on checkout page</Label>
              <span className="text-xs text-zinc-400">(customers will see this coupon listed at checkout)</span>
            </div>
            <Button onClick={handleSave} data-testid="save-coupon-button">Create Coupon</Button>
          </div>
        )}

        {/* ── Coupon list ── */}
        <div className="space-y-2">
          {coupons.map((coupon) => (
            <div key={coupon.coupon_id} data-testid={`coupon-${coupon.coupon_id}`}>

              {/* Row */}
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-mono font-bold text-base">{coupon.code}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {coupon.is_active ? "Active" : "Inactive"}
                    </span>
                    {coupon.show_on_checkout && (
                      <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">Shown at checkout</span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {coupon.discount_type === "percentage" ? `${coupon.discount_value}% off` : `₹${coupon.discount_value} off`}
                    {coupon.min_order_value > 0 && ` · Min ₹${coupon.min_order_value}`}
                    {coupon.expiry_date && ` · Expires ${new Date(coupon.expiry_date).toLocaleDateString('en-IN')}`}
                  </p>
                </div>

                {/* Active toggle */}
                <div className="flex flex-col items-center gap-0.5 shrink-0">
                  <Switch checked={coupon.is_active} onCheckedChange={() => toggleActive(coupon)} />
                  <span className="text-[10px] text-zinc-400">Active</span>
                </div>

                {/* Show on checkout toggle */}
                <div className="flex flex-col items-center gap-0.5 shrink-0">
                  <Switch
                    checked={coupon.show_on_checkout || false}
                    onCheckedChange={() => toggleShowOnCheckout(coupon)}
                    disabled={!coupon.is_active}
                  />
                  <span className="text-[10px] text-zinc-400">Checkout</span>
                </div>

                {/* Edit button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => editingId === coupon.coupon_id ? cancelEdit() : startEdit(coupon)}
                  className="shrink-0"
                  title="Edit coupon"
                >
                  <Pencil size={14} />
                </Button>

                {/* Delete button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(coupon.coupon_id)}
                  className="text-red-600 shrink-0"
                >
                  <Trash2 size={14} />
                </Button>
              </div>

              {/* Inline edit form — shown only for the coupon being edited */}
              {editingId === coupon.coupon_id && (
                <div className="mt-1 p-5 border border-zinc-300 rounded-lg bg-zinc-50 space-y-4">
                  <p className="text-sm font-medium text-zinc-700">Edit Coupon</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Coupon Code</Label>
                      <Input
                        value={editForm.code}
                        onChange={(e) => setEditForm({...editForm, code: e.target.value.toUpperCase()})}
                        placeholder="e.g. RIVORA20"
                      />
                    </div>
                    <div>
                      <Label>Discount Type</Label>
                      <Select value={editForm.discount_type} onValueChange={(val) => setEditForm({...editForm, discount_type: val})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage (%)</SelectItem>
                          <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Discount Value</Label>
                      <Input
                        type="number"
                        value={editForm.discount_value}
                        onChange={(e) => setEditForm({...editForm, discount_value: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Min Order Value (₹)</Label>
                      <Input
                        type="number"
                        value={editForm.min_order_value}
                        onChange={(e) => setEditForm({...editForm, min_order_value: e.target.value})}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label>Expiry Date</Label>
                      <Input
                        type="datetime-local"
                        value={editForm.expiry_date}
                        onChange={(e) => setEditForm({...editForm, expiry_date: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={editForm.show_on_checkout}
                      onCheckedChange={(val) => setEditForm({...editForm, show_on_checkout: val})}
                      id={`show-checkout-edit-${coupon.coupon_id}`}
                    />
                    <Label htmlFor={`show-checkout-edit-${coupon.coupon_id}`} className="cursor-pointer">
                      Show on checkout page
                    </Label>
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={() => handleEditSave(coupon.coupon_id)} disabled={editSaving}>
                      {editSaving ? "Saving…" : "Save Changes"}
                    </Button>
                    <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
function ContentTab() {
  const DEFAULT_CONTACT = { email: "hello@rivorajewels.com", phone: "+91 99999 99999", whatsapp: "919999999999", address: "Johri Bazaar, Jaipur\nRajasthan – 302003, India" };
  const DEFAULT_SOCIAL = { instagram: "https://instagram.com/rivorajewels", facebook: "https://facebook.com/rivorajewels", gmail: "hello@rivorajewels.com" };

  const [contactContent, setContactContent] = useState(DEFAULT_CONTACT);
  const [socialContent, setSocialContent] = useState(DEFAULT_SOCIAL);
  const [savingContact, setSavingContact] = useState(false);
  const [savingSocial, setSavingSocial] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/content/contact`, { withCredentials: true })
      .then(({ data }) => {
        if (data.content && Object.keys(data.content).length > 0) {
          setContactContent({ ...DEFAULT_CONTACT, ...data.content });
        }
      })
      .catch(() => {});

    axios.get(`${API}/admin/content/social`, { withCredentials: true })
      .then(({ data }) => {
        if (data.content && Object.keys(data.content).length > 0) {
          setSocialContent({ ...DEFAULT_SOCIAL, ...data.content });
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      await axios.put(`${API}/admin/content/contact`, { section: "contact", content: contactContent }, { withCredentials: true });
      toast.success("Contact info updated!");
    } catch (error) {
      toast.error("Failed to save contact info");
    } finally {
      setSavingContact(false);
    }
  };

  const handleSaveSocial = async () => {
    setSavingSocial(true);
    try {
      await axios.put(`${API}/admin/content/social`, { section: "social", content: socialContent }, { withCredentials: true });
      toast.success("Social links updated!");
    } catch (error) {
      toast.error("Failed to save social links");
    } finally {
      setSavingSocial(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Contact Information */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Settings size={16} /> Contact Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Business Email</Label>
            <Input value={contactContent.email} onChange={(e) => setContactContent({ ...contactContent, email: e.target.value })} data-testid="content-email" />
          </div>
          <div>
            <Label>Phone Number</Label>
            <Input value={contactContent.phone} onChange={(e) => setContactContent({ ...contactContent, phone: e.target.value })} data-testid="content-phone" />
          </div>
          <div>
            <Label>WhatsApp Number</Label>
            <Input value={contactContent.whatsapp} onChange={(e) => setContactContent({ ...contactContent, whatsapp: e.target.value })} placeholder="917240130440" data-testid="content-whatsapp" />
            <p className="text-xs text-zinc-400 mt-1">Enter number with country code, no spaces or +. Example: 917240130440</p>
          </div>
          <div>
            <Label>Address</Label>
            <Textarea value={contactContent.address} onChange={(e) => setContactContent({ ...contactContent, address: e.target.value })} rows={3} data-testid="content-address" />
          </div>
          <Button onClick={handleSaveContact} disabled={savingContact} data-testid="save-content-button">
            {savingContact ? "Saving..." : "Save Contact Info"}
          </Button>
        </CardContent>
      </Card>

      {/* Social Media Links */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Settings size={16} /> Social Media Links</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Instagram URL</Label>
            <Input value={socialContent.instagram} onChange={(e) => setSocialContent({ ...socialContent, instagram: e.target.value })} placeholder="https://instagram.com/rivorajewels" data-testid="content-instagram" />
            <p className="text-xs text-zinc-400 mt-1">Enter full URL including https://</p>
          </div>
          <div>
            <Label>Facebook URL</Label>
            <Input value={socialContent.facebook} onChange={(e) => setSocialContent({ ...socialContent, facebook: e.target.value })} placeholder="https://facebook.com/rivorajewels" data-testid="content-facebook" />
            <p className="text-xs text-zinc-400 mt-1">Enter full URL including https://</p>
          </div>
          <div>
            <Label>Gmail / Email Address</Label>
            <Input value={socialContent.gmail} onChange={(e) => setSocialContent({ ...socialContent, gmail: e.target.value })} placeholder="hello@rivorajewels.com" data-testid="content-gmail" />
          </div>
          <Button onClick={handleSaveSocial} disabled={savingSocial} data-testid="save-social-button">
            {savingSocial ? "Saving..." : "Save Social Links"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Messages Tab ────────────────────────────────────────────────────────────

function MessagesTab() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/contact-messages`, { withCredentials: true });
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Could not load messages. Check that the backend is running.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/contact-messages/${id}`, { status }, { withCredentials: true });
      setMessages((prev) => prev.map((m) => m.message_id === id ? { ...m, status } : m));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const statusColor = {
    unread:  "bg-red-100 text-red-700",
    read:    "bg-zinc-100 text-zinc-600",
    replied: "bg-green-100 text-green-700",
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleString("en-IN", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const safeEncode = (str) => {
    try { return encodeURIComponent(str || ""); }
    catch { return ""; }
  };

  if (loading) return <div className="py-12 text-center text-sm text-zinc-400">Loading messages…</div>;
  if (error)   return <div className="py-12 text-center text-sm text-red-500">{error}</div>;
  if (!messages.length) return <div className="py-12 text-center text-sm text-zinc-400">No contact messages yet.</div>;

  const unreadCount = messages.filter(m => m.status === "unread").length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-zinc-500">
          {messages.length} message{messages.length !== 1 ? "s" : ""} total
          {unreadCount > 0 && <span className="ml-2 bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">{unreadCount} unread</span>}
        </p>
      </div>
      {messages.map((msg) => {
        if (!msg || !msg.message_id) return null;
        const statusClass = statusColor[msg.status] || "bg-zinc-100 text-zinc-600";
        return (
          <Card key={msg.message_id} className={msg.status === "unread" ? "border-l-4 border-l-zinc-900" : ""}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="font-medium text-sm text-zinc-900">{msg.name || "—"}</p>
                    <span className="text-zinc-400 text-xs">{msg.email || "—"}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-medium ${statusClass}`}>
                      {msg.status || "unknown"}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-400 mb-2">
                    {msg.subject || "No subject"} · {formatDate(msg.created_at)}
                  </p>
                  {expanded === msg.message_id && (
                    <p className="text-sm text-zinc-700 whitespace-pre-wrap bg-zinc-50 p-3 rounded mt-2">
                      {msg.message || "—"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                  <button
                    onClick={() => setExpanded(expanded === msg.message_id ? null : msg.message_id)}
                    className="text-xs text-zinc-500 hover:text-zinc-900 underline"
                  >
                    {expanded === msg.message_id ? "Hide" : "View"}
                  </button>
                  <a
                    href={`mailto:${msg.email || ""}?subject=Re: ${safeEncode(msg.subject)}`}
                    className="text-xs bg-zinc-900 text-white px-3 py-1.5 hover:bg-zinc-700 transition-colors"
                  >
                    Reply
                  </a>
                  {msg.status !== "replied" && (
                    <button
                      onClick={() => updateStatus(msg.message_id, "replied")}
                      className="text-xs text-green-600 hover:text-green-800 border border-green-200 px-2 py-1"
                    >
                      ✓ Replied
                    </button>
                  )}
                  {msg.status === "unread" && (
                    <button
                      onClick={() => updateStatus(msg.message_id, "read")}
                      className="text-xs text-zinc-400 hover:text-zinc-600 border border-zinc-200 px-2 py-1"
                    >
                      Mark Read
                    </button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
